import { defineStore } from 'pinia'
import { ElNotification } from 'element-plus'
import router from '@/router'
import { getCsSessionUnreadTotal } from '@/api/cs-session'
import { onRealtimeEvent, connectAdminRealtime, disconnectAdminRealtime, setRealtimePage } from '@/services/realtime'

const TITLE_FLASH_PREFIX = '(新消息) '

export const useCsNotifyStore = defineStore('csNotify', {
  state: () => ({
    unreadTotal: 0,
    /** 当前客服聊天页已打开的会话 id（用于抑制铃声与未读累加） */
    activeSessionId: '',
    originalTitle: '',
    titleFlashTimer: null,
    visibilityBound: false,
    unsubRealtime: null
  }),

  getters: {
    hasUnread: (s) => s.unreadTotal > 0,
    badgeText: (s) => (s.unreadTotal > 99 ? '99+' : String(s.unreadTotal))
  },

  actions: {
    bindRealtime() {
      if (this.unsubRealtime) return
      this.unsubRealtime = onRealtimeEvent('cs_admin_notify', (data) => this.handleCsAdminNotify(data))
      if (typeof document !== 'undefined' && !this.visibilityBound) {
        this.visibilityBound = true
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            this.stopTitleFlash()
          }
        })
      }
    },

    unbindRealtime() {
      if (typeof this.unsubRealtime === 'function') {
        this.unsubRealtime()
        this.unsubRealtime = null
      }
      this.stopTitleFlash()
    },

    async refreshUnreadTotal() {
      try {
        const res = await getCsSessionUnreadTotal()
        if (res && typeof res.total === 'number') {
          this.unreadTotal = res.total
        }
      } catch (_) {
        /* ignore */
      }
    },

    setActiveSession(id) {
      this.activeSessionId = id ? String(id) : ''
    },

    clearActiveSession() {
      this.activeSessionId = ''
    },

    syncRoute(path) {
      setRealtimePage(path || 'unknown')
    },

    handleCsAdminNotify(payload) {
      if (!payload || typeof payload !== 'object') return
      const sid = payload.sessionId ? String(payload.sessionId) : ''
      const summary = payload.summary ? String(payload.summary) : '新消息'
      const visible = typeof document !== 'undefined' && document.visibilityState === 'visible'
      if (sid && sid === this.activeSessionId && visible) {
        window.dispatchEvent(new CustomEvent('cs:incoming', { detail: payload }))
        return
      }
      this.unreadTotal++
      this.playBeep()
      ElNotification({
        title: '新的客服消息',
        message: summary,
        type: 'info',
        duration: 4500,
        onClick: () => {
          router.push({ path: '/cs/session', query: { sessionId: sid } })
        }
      })
      this.startTitleFlash()
    },

    playBeep() {
      if (typeof window === 'undefined') return
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext
        if (!Ctx) return
        const ctx = new Ctx()
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.value = 880
        gain.gain.value = 0.06
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start()
        osc.stop(ctx.currentTime + 0.09)
        setTimeout(() => {
          try {
            ctx.close()
          } catch (_) {}
        }, 200)
      } catch (_) {
        /* ignore autoplay / AudioContext */
      }
    },

    startTitleFlash() {
      if (typeof document === 'undefined') return
      if (!this.originalTitle) {
        this.originalTitle = document.title || ''
      }
      if (this.titleFlashTimer) return
      let flip = false
      this.titleFlashTimer = window.setInterval(() => {
        flip = !flip
        document.title = flip ? `${TITLE_FLASH_PREFIX}${this.originalTitle}` : this.originalTitle
      }, 1000)
    },

    stopTitleFlash() {
      if (this.titleFlashTimer) {
        clearInterval(this.titleFlashTimer)
        this.titleFlashTimer = null
      }
      if (typeof document !== 'undefined' && this.originalTitle) {
        document.title = this.originalTitle
      }
    },

    /** 登录入口：连接 WS + 订阅 + 拉未读总数 */
    bootstrapFromLogin(token) {
      disconnectAdminRealtime()
      connectAdminRealtime(token)
      this.bindRealtime()
      this.refreshUnreadTotal()
    },

    /** 退出清理 */
    teardown() {
      this.unbindRealtime()
      disconnectAdminRealtime()
      this.clearActiveSession()
      this.unreadTotal = 0
      this.stopTitleFlash()
      this.originalTitle = ''
    }
  }
})
